import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Trash2, MessageSquare, ExternalLink } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function AdminCommentsTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: comments = [], isLoading } = useQuery({ queryKey: ["admin", "comments"], queryFn: endpoints.adminListComments });
  const { data: encComments = [], isLoading: encLoading } = useQuery({ queryKey: ["admin", "encComments"], queryFn: endpoints.adminListEncyclopediaComments });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: endpoints.listTestimonials });

  const goToAcademy = (c) => {
    navigate(`/academy?c=${encodeURIComponent(c.chapter_id)}${c.track_id ? `&t=${encodeURIComponent(c.track_id)}` : ""}#comment-${c.id}`);
  };
  const goToEncyclopedia = (c) => {
    navigate(`/encyclopedia?m=${encodeURIComponent(c.module_id)}#comment-${c.id}`);
  };
  const goToTestimonial = (t) => {
    navigate(`/about#testimonial-${t.id}`);
  };

  const onDeleteComment = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذا التعليق؟" : "Delete this comment?")) return;
    try {
      await endpoints.deleteAcademyComment(id);
      toast.success(isRTL ? "حُذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["admin", "comments"] });
      qc.invalidateQueries({ queryKey: ["academy", "comments"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const onDeleteEncComment = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذا التعليق؟" : "Delete this comment?")) return;
    try {
      await endpoints.deleteEncyclopediaComment(id);
      toast.success(isRTL ? "حُذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["admin", "encComments"] });
      qc.invalidateQueries({ queryKey: ["encyclopedia", "comments"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const onDeleteTestimonial = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذا الرأي؟" : "Delete this testimonial?")) return;
    try {
      await endpoints.deleteTestimonial(id);
      toast.success(isRTL ? "حُذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["testimonials"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const onClearTestimonials = async () => {
    if (!window.confirm(isRTL ? "حذف كل الآراء؟ هذا غير قابل للتراجع." : "Delete ALL testimonials? This cannot be undone.")) return;
    try {
      const res = await endpoints.adminClearTestimonials();
      toast.success(`${isRTL ? "حُذفت" : "Deleted"} ${res.deleted}`);
      qc.invalidateQueries({ queryKey: ["testimonials"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-comments-tab">
      {/* Testimonials moderation */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-[#2A2421]">
            {isRTL ? `آراء الموقع (${testimonials.length})` : `Site testimonials (${testimonials.length})`}
          </h3>
          <Button
            onClick={onClearTestimonials}
            variant="outline"
            className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
            data-testid="admin-clear-testimonials-btn"
          >
            <Trash2 className="w-3.5 h-3.5 me-1" /> {isRTL ? "حذف الكل" : "Delete all"}
          </Button>
        </div>
        <div className="space-y-2">
          {testimonials.length === 0 ? (
            <div className="text-sm text-[#5C544F] p-4 bg-white rounded-xl border border-[#2A2421]/10">
              {isRTL ? "لا توجد آراء" : "No testimonials"}
            </div>
          ) : (
            testimonials.map((t) => (
              <div
                key={t.id}
                className="flex items-start gap-3 bg-white rounded-xl border border-[#2A2421]/10 p-3 hover:border-[#C86240]/40 hover:shadow-sm transition-all cursor-pointer"
                data-testid={`admin-testimonial-${t.id}`}
                onClick={() => goToTestimonial(t)}
                role="link"
                title={isRTL ? "اذهب للموقع الأصلي" : "Open at source"}
              >
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#2A2421] flex items-center gap-1.5">
                    {t.name}{t.cafe && <span className="text-xs text-[#5C544F]"> · {t.cafe}</span>}
                    <ExternalLink className="w-3 h-3 text-[#C86240]/70" />
                  </div>
                  <div className="text-xs text-[#C86240]">{"★".repeat(t.rating || 5)}</div>
                  <div className="text-sm text-[#2A2421] mt-1" dir={t.lang === "ar" ? "rtl" : "ltr"}>{t.text}</div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); onDeleteTestimonial(t.id); }}
                  data-testid={`admin-delete-testimonial-${t.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Academy comments */}
      <section>
        <h3 className="text-lg font-semibold text-[#2A2421] mb-3 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          {isRTL ? `آراء الأكاديمية (${comments.length})` : `Academy comments (${comments.length})`}
        </h3>
        <div className="space-y-2">
          {isLoading && <div className="text-[#5C544F]">…</div>}
          {!isLoading && comments.length === 0 && (
            <div className="text-sm text-[#5C544F] p-4 bg-white rounded-xl border border-[#2A2421]/10">
              {isRTL ? "لم يُكتب رأي بعد" : "No comments yet"}
            </div>
          )}
          {comments.map((c) => (
            <div
              key={c.id}
              className="flex items-start gap-3 bg-white rounded-xl border border-[#2A2421]/10 p-3 hover:border-[#C86240]/40 hover:shadow-sm transition-all cursor-pointer"
              data-testid={`admin-comment-${c.id}`}
              onClick={() => goToAcademy(c)}
              role="link"
              title={isRTL ? "اذهب لمكان التعليق" : "Jump to comment location"}
            >
              {c.user_picture && <img src={c.user_picture} alt="" className="w-8 h-8 rounded-full" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#2A2421] flex items-center gap-1.5">
                  {c.user_name}
                  <ExternalLink className="w-3 h-3 text-[#C86240]/70" />
                </div>
                <div className="text-[10px] text-[#5C544F]">{c.chapter_id} · {new Date(c.created_at).toLocaleString()}</div>
                <div className="text-sm text-[#2A2421] mt-1 whitespace-pre-wrap">{c.text}</div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); onDeleteComment(c.id); }}
                data-testid={`admin-delete-comment-${c.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Encyclopedia comments */}
      <section>
        <h3 className="text-lg font-semibold text-[#2A2421] mb-3 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          {isRTL ? `آراء الموسوعة (${encComments.length})` : `Encyclopedia comments (${encComments.length})`}
        </h3>
        <div className="space-y-2">
          {encLoading && <div className="text-[#5C544F]">…</div>}
          {!encLoading && encComments.length === 0 && (
            <div className="text-sm text-[#5C544F] p-4 bg-white rounded-xl border border-[#2A2421]/10">
              {isRTL ? "لم يُكتب رأي بعد" : "No comments yet"}
            </div>
          )}
          {encComments.map((c) => (
            <div
              key={c.id}
              className="flex items-start gap-3 bg-white rounded-xl border border-[#2A2421]/10 p-3 hover:border-[#C86240]/40 hover:shadow-sm transition-all cursor-pointer"
              data-testid={`admin-enc-comment-${c.id}`}
              onClick={() => goToEncyclopedia(c)}
              role="link"
              title={isRTL ? "اذهب لمكان التعليق" : "Jump to comment location"}
            >
              {c.user_picture && <img src={c.user_picture} alt="" className="w-8 h-8 rounded-full" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#2A2421] flex items-center gap-1.5">
                  {c.user_name}
                  <ExternalLink className="w-3 h-3 text-[#C86240]/70" />
                </div>
                <div className="text-[10px] text-[#5C544F]">{c.module_id} · {new Date(c.created_at).toLocaleString()}</div>
                <div className="text-sm text-[#2A2421] mt-1 whitespace-pre-wrap">{c.text}</div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); onDeleteEncComment(c.id); }}
                data-testid={`admin-delete-enc-comment-${c.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
